import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { hasFrontendPermission } from '../permissions';

const DEFAULT_DISPATCH_SETTINGS = Object.freeze({
  maxJobsPerTechnicianPerDay: 2,
  slaDueSoonDays: 1,
});

const STATUS_LABELS = {
  new: 'New',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed',
  on_hold: 'On Hold',
};

const normalizeDispatchSettings = (value = {}) => {
  const maxJobs = Number(value.maxJobsPerTechnicianPerDay);
  const dueSoonDays = Number(value.slaDueSoonDays);
  return {
    maxJobsPerTechnicianPerDay: Number.isFinite(maxJobs) ? Math.max(1, Math.min(20, Math.floor(maxJobs))) : DEFAULT_DISPATCH_SETTINGS.maxJobsPerTechnicianPerDay,
    slaDueSoonDays: Number.isFinite(dueSoonDays) ? Math.max(0, Math.min(14, Math.floor(dueSoonDays))) : DEFAULT_DISPATCH_SETTINGS.slaDueSoonDays,
  };
};

const toDateAtMidnight = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parts = raw.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const isOpenJobStatus = (status) => String(status || '').toLowerCase() !== 'completed';

const computeDispatchInsights = (jobs, rawSettings = DEFAULT_DISPATCH_SETTINGS) => {
  const settings = normalizeDispatchSettings(rawSettings);
  const today = startOfToday();
  const byTechDay = new Map();
  const conflicts = [];
  const slaRisks = [];
  let unassignedOpen = 0;

  (jobs || []).forEach((job) => {
    const scheduledDate = String(job?.scheduledDate || '').trim();
    const assignedTo = String(job?.assignedTo || '').trim();
    const status = String(job?.status || '').toLowerCase();
    const isOpen = isOpenJobStatus(status);
    const dateObj = toDateAtMidnight(scheduledDate);

    if (isOpen && !assignedTo) unassignedOpen += 1;

    if (isOpen && assignedTo && scheduledDate) {
      const key = `${assignedTo}::${scheduledDate}`;
      const list = byTechDay.get(key) || [];
      list.push(job);
      byTechDay.set(key, list);
    }

    if (!isOpen || !dateObj) return;

    if (dateObj < today) {
      slaRisks.push({ severity: 'high', job });
      return;
    }

    const dueSoonCutoff = new Date(today.getTime() + settings.slaDueSoonDays * 86400000);
    if ((status === 'new' || status === 'assigned') && dateObj <= dueSoonCutoff) {
      slaRisks.push({ severity: 'medium', job });
    }
  });

  for (const [key, list] of byTechDay.entries()) {
    if (list.length <= settings.maxJobsPerTechnicianPerDay) continue;
    const [technician, date] = key.split('::');
    conflicts.push({
      technician,
      date,
      count: list.length,
      severity: list.length - settings.maxJobsPerTechnicianPerDay >= 2 ? 'high' : 'medium',
      jobs: list,
    });
  }

  conflicts.sort((a, b) => b.count - a.count);
  slaRisks.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1));

  return { conflicts, slaRisks, unassignedOpen };
};

const formatScheduleDay = (dateString) => {
  const date = toDateAtMidnight(dateString);
  if (!date) return dateString;
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatScheduleRange = (jobs) => {
  if (!jobs.length) return 'No scheduled work';
  const first = jobs[0]?.scheduledDate;
  const last = jobs[jobs.length - 1]?.scheduledDate;
  if (!first || !last) return 'No date range';
  return `${formatScheduleDay(first)} - ${formatScheduleDay(last)}`;
};

const groupedSchedule = (schedule) => {
  const map = new Map();
  schedule.forEach((job) => {
    if (!job.scheduledDate) return;
    const list = map.get(job.scheduledDate) || [];
    list.push(job);
    map.set(job.scheduledDate, list);
  });
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, jobs]) => ({
      date,
      jobs: [...jobs].sort((a, b) => String(a.assignedTo || '').localeCompare(String(b.assignedTo || ''))),
    }));
};

const suggestionLabel = (item) => {
  if (item.type === 'assign') return `Assign to ${item.suggestedAssignee || 'best technician'}`;
  if (item.type === 'reschedule') return `Move to ${formatScheduleDay(item.suggestedDate)}`;
  return 'Apply suggestion';
};

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ScheduleKpi({ label, value, tone = 'default', sub }) {
  return (
    <article className={`schedule-kpi schedule-kpi--${tone}`}>
      <span className="schedule-kpi-label">{label}</span>
      <strong className="schedule-kpi-value">{value}</strong>
      {sub ? <span className="schedule-kpi-sub">{sub}</span> : null}
    </article>
  );
}

function AgendaItem({ job }) {
  return (
    <article className="schedule-agenda-item">
      <div className="schedule-agenda-title-row">
        <h3 className="schedule-agenda-title">{job.title || 'Untitled job'}</h3>
        <span className={`jobs-pill jobs-pill--status-${job.status || 'new'}`}>
          {STATUS_LABELS[job.status] || job.status || 'New'}
        </span>
      </div>
      <div className="schedule-agenda-meta">
        <span>{job.assignedTo || 'Unassigned'}</span>
        <span>{job.location || 'No location'}</span>
        <span>{job.customerId || 'No customer'}</span>
      </div>
    </article>
  );
}

export function SchedulePage() {
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageDispatch = hasFrontendPermission(user, 'dispatch.manage');

  const [schedule, setSchedule] = useState([]);
  const [dispatchSettings, setDispatchSettings] = useState(DEFAULT_DISPATCH_SETTINGS);
  const [optimization, setOptimization] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [applyingId, setApplyingId] = useState('');
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadSchedule = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [scheduleData, settingsData] = await Promise.all([
        apiFetch('/api/schedule', { token }),
        apiFetch('/api/settings/dispatch', { token }),
      ]);
      setSchedule(Array.isArray(scheduleData) ? scheduleData : []);
      setDispatchSettings(normalizeDispatchSettings(settingsData || {}));
    } catch (e) {
      setError(e.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadOptimization = useCallback(async () => {
    if (!token || !canManageDispatch) return;
    try {
      const data = await apiFetch('/api/dispatch/optimize', { token });
      setOptimization(data || null);
    } catch (e) {
      setError(e.message || 'Failed to load optimization suggestions');
    }
  }, [token, canManageDispatch]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule, refreshKey]);

  useEffect(() => {
    if (!loading && canManageDispatch) {
      loadOptimization();
    }
  }, [loading, canManageDispatch, loadOptimization, refreshKey]);

  const groups = useMemo(() => groupedSchedule(schedule), [schedule]);

  useEffect(() => {
    if (!groups.length) {
      setSelectedDate('');
      return;
    }
    if (!groups.some((group) => group.date === selectedDate)) {
      setSelectedDate(groups[0].date);
    }
  }, [groups, selectedDate]);

  const selectedGroup = useMemo(
    () => groups.find((group) => group.date === selectedDate) || null,
    [groups, selectedDate],
  );

  const insights = useMemo(
    () => computeDispatchInsights(schedule, dispatchSettings),
    [schedule, dispatchSettings],
  );
  const handleRefresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  const quickActions = useMemo(() => {
    const actions = [];
    if (insights.conflicts.length > 0) {
      actions.push({
        label: 'Review Conflicts',
        onClick: () => {
          const firstConflict = insights.conflicts[0];
          if (firstConflict?.date) setSelectedDate(firstConflict.date);
        },
      });
    }
    if (insights.unassignedOpen > 0) {
      actions.push({
        label: 'Review Unassigned',
        onClick: () => {
          const firstUnassigned = schedule.find((job) => isOpenJobStatus(job.status) && !job.assignedTo && job.scheduledDate);
          if (firstUnassigned?.scheduledDate) setSelectedDate(firstUnassigned.scheduledDate);
        },
      });
    }
    if (canManageDispatch) {
      actions.push({
        label: 'Refresh Optimization',
        onClick: handleRefresh,
      });
    }
    return actions.slice(0, 3);
  }, [insights.conflicts, insights.unassignedOpen, canManageDispatch, schedule, handleRefresh]);

  const handleSaveSettings = async (event) => {
    event.preventDefault();
    if (!token || !canManageDispatch) return;
    setSavingSettings(true);
    setError('');
    try {
      const payload = normalizeDispatchSettings(dispatchSettings);
      const saved = await apiFetch('/api/settings/dispatch', {
        token,
        method: 'PUT',
        body: payload,
      });
      setDispatchSettings(normalizeDispatchSettings(saved || payload));
      pushToast('Dispatch settings saved', 'success');
      setRefreshKey((value) => value + 1);
    } catch (e) {
      setError(e.message || 'Failed to save dispatch settings');
      pushToast(e.message || 'Failed to save dispatch settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleApplySuggestion = async (item) => {
    if (!token || !item?.id) return;
    setApplyingId(item.id);
    setError('');
    try {
      await apiFetch('/api/dispatch/optimize/apply', {
        token,
        method: 'POST',
        body: {
          jobId: item.jobId,
          type: item.type,
          suggestedAssignee: item.suggestedAssignee,
          suggestedDate: item.suggestedDate,
        },
      });
      pushToast(`Applied optimization for ${item.jobId}`, 'success');
      setRefreshKey((value) => value + 1);
    } catch (e) {
      setError(e.message || 'Failed to apply optimization');
      pushToast(e.message || 'Failed to apply optimization', 'error');
    } finally {
      setApplyingId('');
    }
  };

  if (loading) {
    return (
      <section className="schedule-shell">
        <Skeleton lines={12} />
      </section>
    );
  }

  return (
    <section className="schedule-shell">
      <div className="schedule-header">
        <div>
          <span className="schedule-eyebrow">Operations</span>
          <h1 className="schedule-heading">Schedule</h1>
          <p className="schedule-subheading">
            Daily workload planning, dispatch pressure, and optimization actions across the field team.
          </p>
        </div>
        <div className="schedule-header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon />
            Refresh
          </Button>
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadSchedule} /> : null}

      <Card className="workflow-guide-card" title="What To Do Here">
        <div className="workflow-guide-head">
          <p className="workflow-guide-copy">
            Use Schedule to balance the day, resolve technician overload, and catch delivery risk before it becomes a missed visit.
          </p>
          <div className="workflow-guide-actions">
            {quickActions.map((action) => (
              <Button key={action.label} variant="secondary" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="workflow-guide-list">
          <div className="workflow-guide-item">Start with conflicts, SLA risks, and unassigned work before scanning the full calendar.</div>
          <div className="workflow-guide-item">Use the agenda rail to focus one day at a time and confirm who owns each scheduled visit.</div>
          <div className="workflow-guide-item">Adjust dispatch rules or apply optimization suggestions only after you understand the day-level pressure.</div>
        </div>
      </Card>

      <div className="schedule-kpi-grid">
        <ScheduleKpi label="Scheduled Jobs" value={schedule.length} sub={formatScheduleRange(schedule)} />
        <ScheduleKpi label="Days Planned" value={groups.length} tone="accent" />
        <ScheduleKpi label="Conflicts" value={insights.conflicts.length} tone="warning" />
        <ScheduleKpi label="SLA Risks" value={insights.slaRisks.length} tone="danger" />
        <ScheduleKpi label="Unassigned" value={insights.unassignedOpen} tone="info" />
      </div>

      <div className="schedule-layout">
        <Card className="schedule-rail-card" title="Agenda Days">
          {groups.length ? (
            <div className="schedule-day-list">
              {groups.map((group) => (
                <button
                  key={group.date}
                  type="button"
                  className={`schedule-day-button${group.date === selectedDate ? ' is-active' : ''}`}
                  onClick={() => setSelectedDate(group.date)}
                >
                  <span className="schedule-day-label">{formatScheduleDay(group.date)}</span>
                  <span className="schedule-day-count">{group.jobs.length} jobs</span>
                </button>
              ))}
            </div>
          ) : (
            <EmptyState title="No scheduled jobs" message="Add schedule dates to jobs to populate the agenda." />
          )}
        </Card>

        <Card className="schedule-main-card" title={selectedGroup ? formatScheduleDay(selectedGroup.date) : 'Daily Agenda'}>
          {selectedGroup ? (
            <div className="schedule-agenda">
              <div className="schedule-agenda-summary">
                <span>{selectedGroup.jobs.length} jobs scheduled</span>
                <span>{selectedGroup.jobs.filter((job) => !job.assignedTo).length} unassigned</span>
              </div>
              {selectedGroup.jobs.map((job) => <AgendaItem key={job.id} job={job} />)}
            </div>
          ) : (
            <EmptyState title="No day selected" message="Choose a date from the agenda rail to review scheduled work." />
          )}
        </Card>

        <div className="schedule-side-stack">
          <Card title="Risk Board">
            <div className="schedule-risk-list">
              {insights.conflicts.slice(0, 4).map((conflict) => (
                <button
                  key={`${conflict.technician}-${conflict.date}`}
                  type="button"
                  className={`schedule-risk-item schedule-risk-item--${conflict.severity}`}
                  onClick={() => navigate(`/jobs?riskType=conflict&technician=${encodeURIComponent(conflict.technician)}&date=${encodeURIComponent(conflict.date)}`)}
                >
                  <strong>{conflict.technician}</strong>
                  <span>{conflict.count} jobs on {formatScheduleDay(conflict.date)}</span>
                </button>
              ))}
              {insights.slaRisks.slice(0, 4).map((risk) => (
                <button
                  key={risk.job.id}
                  type="button"
                  className={`schedule-risk-item schedule-risk-item--${risk.severity}`}
                  onClick={() => navigate(`/jobs`)}
                >
                  <strong>{risk.job.id}</strong>
                  <span>{risk.job.title}</span>
                </button>
              ))}
              {!insights.conflicts.length && !insights.slaRisks.length && !insights.unassignedOpen ? (
                <EmptyState title="No active risks" message="The current schedule is balanced against the configured rules." />
              ) : null}
            </div>
          </Card>

          {canManageDispatch ? (
            <Card title="Dispatch Rules">
              <form className="schedule-settings-form" onSubmit={handleSaveSettings}>
                <label className="jobs-field">
                  <span className="jobs-field-label">Max Jobs / Tech / Day</span>
                  <input
                    className="jobs-input"
                    type="number"
                    min="1"
                    max="20"
                    value={dispatchSettings.maxJobsPerTechnicianPerDay}
                    onChange={(event) => setDispatchSettings((prev) => ({ ...prev, maxJobsPerTechnicianPerDay: event.target.value }))}
                  />
                </label>
                <label className="jobs-field">
                  <span className="jobs-field-label">SLA Due Soon Window</span>
                  <input
                    className="jobs-input"
                    type="number"
                    min="0"
                    max="14"
                    value={dispatchSettings.slaDueSoonDays}
                    onChange={(event) => setDispatchSettings((prev) => ({ ...prev, slaDueSoonDays: event.target.value }))}
                  />
                </label>
                <Button type="submit" disabled={savingSettings}>Save Rules</Button>
              </form>
            </Card>
          ) : null}

          {canManageDispatch ? (
            <Card title="Optimization">
              {optimization?.suggestions?.length ? (
                <div className="schedule-optimization-list">
                  {optimization.suggestions.slice(0, 6).map((item) => (
                    <div className="schedule-optimization-item" key={item.id}>
                      <div>
                        <strong className="schedule-optimization-job">{item.jobId}</strong>
                        <p className="schedule-optimization-copy">{item.reason || suggestionLabel(item)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleApplySuggestion(item)}
                        disabled={applyingId === item.id}
                      >
                        {suggestionLabel(item)}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No suggestions" message="No optimization actions are currently needed." />
              )}
            </Card>
          ) : null}
        </div>
      </div>
    </section>
  );
}
